import { ComponentType, useEffect, useState } from 'react';
import { Country, CountryCode } from '../../api';
import { CustomSelectProps, Select } from '../atoms/Select';
import { SelectChangeEvent } from '../hocs';

export interface CountrySelectBaseProps {
  name: string;
  placeholder?: string;
  defaultCountry?: CountryCode;
  countries: Country[];
  loadingMessage?: string;
  errorMessage?: string;
  /**
   * Use only if you need to tweak styles of select and options
   * For Selects with completely different behavior (errors, validation, loading, additional elements),
   * use HOCs
   */
  CustomSelect?: ComponentType<CustomSelectProps>;
}

const CountrySelectBase = ({
  name = 'country', countries, placeholder, defaultCountry, loadingMessage, errorMessage, CustomSelect
}: CountrySelectBaseProps) => {
  const [country, setCountry] = useState(defaultCountry);

  useEffect(() => {
    if (!defaultCountry && countries.length > 0) {
      setCountry(countries[0].code);
    }
    if (defaultCountry) setCountry(defaultCountry);
  }, [countries, defaultCountry]);

  const makeOptions = (countries: Country[]) => {
    if (loadingMessage) return [{ label: loadingMessage, value: '' }];
    if (errorMessage) return [{ label: errorMessage, value: '' }];

    const options = countries
      .map(({ name, code, nativeName }) => ({ label: nativeName ? nativeName : name, value: code }));


    if (placeholder) {
      return [{ value: placeholder, label: placeholder }, ...options]
    } if (options.length === 0) {
      return [{ label: '', value: '' }];
    }
    else {
      return options;
    }
  }

  const handleChange = (event: SelectChangeEvent) => {
    const countrySelected = new CustomEvent('mist-com-country-selected', {
      detail: { countryCode: event?.target.value },
      bubbles: true
    });
    document.dispatchEvent(countrySelected);
  };

  const initialValue = placeholder || country;
  return <Select
    defaultValue={initialValue}
    name={name}
    options={makeOptions(countries)}
    onChange={handleChange}
    disabled={countries.length <= 1}
    CustomSelect={CustomSelect}
  />;
}

export default CountrySelectBase;
